import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleMap, Marker, Circle, InfoWindow } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { useBreakpoint, Icon } from '@panel/commons';
import infoIcon from '@panel/commons/assets/icons/information_icon.svg';
import { site } from '@panel/commons/helpers';
import customMarker from '../../../../assets/img/custom_marker.svg';
import { Step1Module2Selectors, Step1Module2Operations } from '../../../ducks/step1Module2';
import { setCoordinates } from '../../../ducks/step1Module2/operations';
import {
  ContainerMap,
  MapAddress,
  MapWrapper,
  InfoWindowText,
  VisibilityOptionsAddressWrapper,
  VisibilityOptionsContainer,
  InfoBox,
  Title,
  TitleWrapper
} from './styles';
import { CustomCheckBox } from '../../commons/checkbox';

const siteId = site.id;
const addressSeparator = ['IWBR', 'WIBR', 'ICBR'].includes(siteId) ? '-' : ',';
const options = {
  disableDefaultUI: true,
  zoomControl: true
};
const radiusOptions = {
  fillColor: '#C60024',
  fillOpacity: '0.2',
  strokeOpacity: '0'
};
const visibilityOptions = [
  { id: '0', name: 'addressType', reference: 'exact', value: 'EXACT' },
  { id: '1', name: 'addressType', reference: 'zone', value: 'ZONE' }
];
const regex = ['IWBR', 'WIBR', 'ICBR'].includes(siteId) ? /(.*)(,\s\d+$)/gm : /(.*)(\s\d+$)/gm;

export const MapSection = ({ isLoaded, loadError, register }) => {
  const [viewInfoWindow, setViewInfoWindow] = useState(true);
  const [formatedAddress, setFormatedAddress] = useState('');
  const { hasCoordinates, formValues, visibility } = useSelector(Step1Module2Selectors.getStep1Module2State);
  const [reachedBreakPoint] = useBreakpoint(1280);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { address, selectedProvince, selectedCity, selectedDistrict, selectedSubDistrict } = formValues;
  const { lat, lng } = hasCoordinates;

  const mapStyles = {
    height: `${reachedBreakPoint ? 440 : 320}px`,
    width: '100%'
  };
  useEffect(() => {
    if (address) {
      let formatAddress = address.split(addressSeparator)[0].trim();
      if (visibility === 'ZONE') {
        const execRegex = regex.exec(formatAddress);
        if (execRegex && execRegex.length > 2) {
          formatAddress = [execRegex[1]];
        }
      }
      if (selectedSubDistrict && selectedDistrict) {
        formatAddress = `${formatAddress}, ${selectedSubDistrict.name}, ${selectedDistrict.name}`;
      } else if (selectedDistrict && selectedCity) {
        formatAddress = `${formatAddress}, ${selectedDistrict.name}, ${selectedCity.name}`;
      } else if (selectedCity && selectedProvince) {
        formatAddress = `${formatAddress}, ${selectedCity.name}, ${selectedProvince.name}`;
      }
      setFormatedAddress(formatAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, selectedSubDistrict, selectedDistrict, selectedCity, selectedProvince, visibility]);

  const handleOptionOnChange = () => {
    dispatch(Step1Module2Operations.setVisibility(visibility === 'ZONE' ? 'EXACT' : 'ZONE'));
  };

  const handleOnDrag = onDrag => {
    const { lat: newLat, lng: newLng } = onDrag.latLng;
    const coordinates = { lat: newLat(), lng: newLng() };
    setViewInfoWindow(true);
    dispatch(setCoordinates(coordinates));
  };

  const onDragHideInfoWindow = () => {
    setViewInfoWindow(false);
  };

  if (!isLoaded) {
    return 'Loading Maps';
  }

  return (
    <ContainerMap data-repa='Step1Modulo2-mapSection-container'>
      {selectedCity && (lat || lng) && (
        <>
          <TitleWrapper>
            <Title data-repa='Step1Modulo2-mapSection-title'>{t('publisher.step1.module2.map.title')}</Title>
          </TitleWrapper>
          <VisibilityOptionsAddressWrapper>
            <VisibilityOptionsContainer>
              {visibilityOptions.map(({ id, name, reference, value }) => (
                <CustomCheckBox
                  isRadioButtom
                  data-repa='Step1Modulo2-visibility-options'
                  key={`visibilityKey-${reference}-${id}`}
                  className={`${reference}`}
                >
                  <div>
                    <label htmlFor={`${reference}`}>
                      <input
                        key={`visibilityKey-${reference}`}
                        type='radio'
                        name={name}
                        ref={register}
                        value={value}
                        id={reference}
                        checked={value === visibility}
                        onChange={handleOptionOnChange}
                      />
                      <span>{t(`publisher.step1.module2.visibilityAddress.${reference}`)}</span>
                    </label>
                  </div>
                </CustomCheckBox>
              ))}
            </VisibilityOptionsContainer>
            <MapAddress data-repa='Step1Modulo2-mapSection-mapAddress'>
              <Icon name='location' width={15} height={15} viewBox='0 0 16 16' colorFill='#000000' />
              <span data-repa='Step1Modulo2-mapSection-formatedAddress'>{formatedAddress}</span>
            </MapAddress>
          </VisibilityOptionsAddressWrapper>
          <MapWrapper>
            <GoogleMap
              data-repa='Step1Modulo2-mapSection-googleMapContainer'
              mapContainerStyle={mapStyles}
              zoom={16}
              center={{ lat, lng }}
              options={options}
              radius={16}
            >
              {visibility === 'EXACT' && (
                <Marker
                  position={{ lat, lng }}
                  draggable
                  onDragEnd={handleOnDrag}
                  onDrag={onDragHideInfoWindow}
                  icon={{ url: customMarker }}
                />
              )}
              {visibility === 'EXACT' && viewInfoWindow && (
                <InfoWindow position={{ lat, lng }} options={{ pixelOffset: new window.google.maps.Size(0, 80) }}>
                  <div>
                    <InfoWindowText>{t('publisher.step1.module2.map.dragMarker')}</InfoWindowText>
                  </div>
                </InfoWindow>
              )}
              {visibility === 'ZONE' && <Circle center={{ lat, lng }} radius={250} options={radiusOptions} />}
            </GoogleMap>
            {loadError && <p data-repa='Step1Modulo2-mapSection-laodingMapError'>Error loading map</p>}
          </MapWrapper>
          <InfoBox data-repa='Step1Modulo2-mapSection-infoBox'>
            <div>
              <img src={infoIcon} alt='information' />
            </div>
            <p>{t('publisher.step1.module2.approximateAddressBox')}</p>
          </InfoBox>
        </>
      )}
    </ContainerMap>
  );
};
